import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchResultsList } from '../../components/panels/SearchResultsList';
import { PaperPiecesContext } from '../../components/panels/paperPieces';
import type { DrawingDocument } from '@peruselab/structure-editor/model';
import { SearchStatusHeader } from '../../components/panels/SearchStatusHeader';
import type { SearchLike } from '../../hooks/searchRequestState';
import type { SearchResults } from '@peruselab/api-client/moleculeSearch';
import type { SearchResultFilters } from '../../hooks/useSearchResultFilters';
import type { EntityType } from '../../components/panels/searchResultsModel';
import { MyDrawingsFeed } from '../../components/panels/MyDrawingsFeed';
import type { MyDrawingsFeed as Feed } from '../../hooks/useMyDrawingsFeed';
import type { MoleculeDrawingSummary } from '@peruselab/api-client/moleculeDrawings';
import { ReactionHitCard } from '../../components/panels/ReactionResultCard';
import type { ReactionQueryRole, ReactionSearchHit } from '@peruselab/api-client/reactionSearch';

interface ResultsRegionProps {
  // The drawing paper, rendered as the mosaic's first (2x2-or-larger-spanning) grid item,
  // alongside every result card -- see `.workspace-mosaic` in styles.css.
  // A slot rather than ResultsRegion owning/importing DrawingPaper itself: WorkspaceShell already
  // wires DrawingPaper's large prop surface (document state, tool, selection, all the mutation
  // callbacks), and none of that is otherwise relevant to search results.
  readonly paper: ReactNode;
  readonly search: SearchLike<SearchResults>;
  /** #1178: the pieces of the paper behind Identical structure matches (their cards mirror them). */
  readonly identicalPieces?: Readonly<Record<string, DrawingDocument>> | undefined;
  readonly filters: SearchResultFilters;
  readonly showingPreviousResults?: boolean;
  readonly idleKey?: string | undefined;
  readonly onOpen?: ((id: string, kind: EntityType) => void) | undefined;
  readonly openDisabled?: boolean | undefined;
  // #658 -- a line above the results saying what kind of search ran (Markush match vs core-only).
  readonly note?: string | undefined;
  // #682 -- present while the canvas is empty in Drawing mode: the user's own drawings take the
  // results' place (and drive the status line), until something is drawn.
  readonly myDrawings?: Feed | undefined;
  /** #837 -- the feed's Archived view: each card offers Restore. */
  readonly onRestoreDrawing?: ((drawing: MoleculeDrawingSummary) => void) | undefined;
  /** #705 -- shown right under the drawing paper (the reaction's Conditions card). */
  readonly underPaper?: ReactNode | undefined;
  /** #1183 -- source image comparison card beside the drawing paper upon OCSR completion. */
  readonly sourceImageCard?: ReactNode | undefined;
  /**
   * #704 -- reactions found for the paper's reaction (arrow and boxes), shown ahead of the other
   * results. #743 -- present while an arrow is on the paper.
   */
  readonly reactionResults?:
    | {
        readonly hits: readonly ReactionSearchHit[];
        readonly formulaOf: (role: ReactionQueryRole, index: number) => string;
        readonly loading?: boolean | undefined;
        /** #743 -- the arrow is the only thing on the paper: it is the whole query, so only
         *  reactions of its kind are shown. */
        readonly arrowOnly?: boolean | undefined;
        /** Set when the reaction search failed: the status line offers this retry. */
        readonly onRetry?: (() => void) | undefined;
      }
    | undefined;
}

/** #743 -- the reaction search's loading state in the shape SearchStatusHeader reads (thin bar);
 *  its failure is said by the header's own reaction line (onRetryReactionSearch). */
function reactionStatus(loading: boolean): SearchLike<SearchResults> {
  return {
    search: loading ? { state: 'loading', sourceKey: 'reactions' } : { state: 'idle' },
    currentResults: null,
    searchIsStale: false,
    runSearch: async () => undefined
  };
}

/** The feed's loading / error state in the shape SearchStatusHeader reads (thin bar, Retry). */
function feedStatus(feed: Feed): SearchLike<SearchResults> {
  const { status } = feed;
  return {
    search:
      status.state === 'loading'
        ? { state: 'loading', sourceKey: 'my-drawings' }
        : status.state === 'error'
          ? { state: 'error', sourceKey: 'my-drawings', message: status.message }
          : { state: 'idle' },
    currentResults: null,
    searchIsStale: false,
    runSearch: async () => feed.retry()
  };
}

// ADR 0007 P6 — search results in the main content area, now sharing one card mosaic with the
// drawing paper (legacy-GWT-parity redesign): the paper is a large card exactly 4x (or more, for
// an oversized import) the area of a result card, and every match -- any entity type, no
// per-type grouping -- flows around it in one grid, most-similar-first (each card's own
// CategoryBadge is the only Identical/Similar signal; there's no separate lead block). The
// status header (match count / error+retry) stays outside the grid, above it, since it's a status
// bar, not a card. The filter rail lives in the FILTER tab (P6b). Drawing matches can be
// opened straight into the editor (P10d). Generalized beyond
// Drawing-only search in #463 -- WorkspaceShell picks which mode's SearchLike view and copy keys
// to hand in. #518 -- the sponsor-card placeholder (real sponsor serving is still ADR 0005 Phase
// 6, not built yet) was removed from the mosaic entirely rather than left as filler.
const NO_PIECES: Readonly<Record<string, DrawingDocument>> = {};

export function ResultsRegion({
  paper,
  search,
  identicalPieces,
  filters,
  showingPreviousResults = false,
  idleKey,
  onOpen,
  openDisabled,
  note,
  myDrawings,
  onRestoreDrawing,
  reactionResults,
  underPaper,
  sourceImageCard
}: ResultsRegionProps) {
  const { t } = useTranslation();
  return (
    <section className="workspace-results" data-testid="results-region" aria-label={t('workspace.results.label')}>
      {myDrawings ? (
        <SearchStatusHeader search={feedStatus(myDrawings)} />
      ) : (
        <SearchStatusHeader
          search={reactionResults?.arrowOnly ? reactionStatus(reactionResults.loading === true) : search}
          showingPreviousResults={showingPreviousResults}
          idleKey={idleKey}
          note={note}
          onRetryReactionSearch={reactionResults?.onRetry}
        />
      )}
      <div
        aria-busy={showingPreviousResults}
        data-previous-results={showingPreviousResults || undefined}
        className={underPaper ? 'workspace-mosaic workspace-mosaic--with-conditions' : 'workspace-mosaic'}
      >
        {paper}
        {sourceImageCard}
        {underPaper}
        {myDrawings ? (
          <MyDrawingsFeed
            feed={myDrawings}
            onOpen={(id) => onOpen?.(id, 'drawings')}
            openDisabled={openDisabled}
            onRestore={onRestoreDrawing}
          />
        ) : (
          <>
            {/* #704: with a reaction arrow on the paper, the reactions found lead the results. */}
            {reactionResults?.hits.map((hit) => (
              <ReactionHitCard
                key={hit.reactionId}
                hit={hit}
                formulaOf={reactionResults.formulaOf}
                onOpen={onOpen ? (id) => onOpen(id, 'reactions') : undefined}
                openDisabled={openDisabled}
              />
            ))}
            {reactionResults?.arrowOnly ? (
              // #743: the arrow alone is the query -- only reactions of its kind, nothing else.
              !reactionResults.loading && !reactionResults.onRetry && reactionResults.hits.length === 0 ? (
                <p data-testid="reaction-results-none">{t('reactionResults.noneForArrow')}</p>
              ) : null
            ) : (
              <PaperPiecesContext.Provider value={identicalPieces ?? NO_PIECES}>
                <SearchResultsList
                  filters={filters}
                  // #743: with an arrow, reactions come from the reaction search (its kind, its boxes)
                  // alone -- the plain search's reaction cards would repeat them, or break the kind.
                  hideReactions={reactionResults !== undefined}
                  // #704: "No matches found" is about molecules; with reactions found, it would contradict them.
                  hasResults={search.currentResults !== null && !reactionResults?.hits.length}
                  onOpen={onOpen}
                  openDisabled={openDisabled}
                />
              </PaperPiecesContext.Provider>
            )}
          </>
        )}
      </div>
    </section>
  );
}
